/**
 * Storage Validation Utilities
 *
 * Provides functions to check user's current storage usage and validate
 * against defined limits before allowing file uploads.
 */

import { collection, getDocs, query, where } from 'firebase/firestore';
import {
  calculateUsagePercentage,
  FILE_COUNT_LIMITS,
  FILE_SIZE_LIMITS,
  formatBytes,
  isApproachingLimit,
  isLimitExceeded,
} from '../config/uploadLimits';
import { firestore } from '../services/firebase';

export interface StorageUsage {
  // File counts
  totalFiles: number;
  cvCount: number;
  caseStudyCount: number;
  companyInfoCount: number;
  pastProposalCount: number;

  // Storage size (in bytes)
  totalSize: number;
  cvSize: number;
  caseStudySize: number;
  companyInfoSize: number;
  pastProposalSize: number;
}

export interface TenderStorageUsage {
  supportDocsCount: number;
  draftsCount: number;
  cvCount: number;
  formsCount: number;
  totalSize: number;
}

export interface ValidationResult {
  allowed: boolean;
  reason?: string;
  currentUsage?: number;
  maxLimit?: number;
  warningMessage?: string | undefined;
}

/**
 * Get user's current library storage usage across all categories
 */
export const getUserLibraryStorageUsage = async (
  userId: string,
  clientId: string
): Promise<StorageUsage> => {
  if (!userId || !clientId) {
    throw new Error('User ID and Client ID are required to fetch storage usage');
  }

  const usage: StorageUsage = {
    totalFiles: 0,
    cvCount: 0,
    caseStudyCount: 0,
    companyInfoCount: 0,
    pastProposalCount: 0,
    totalSize: 0,
    cvSize: 0,
    caseStudySize: 0,
    companyInfoSize: 0,
    pastProposalSize: 0,
  };

  try {
    // Fetch Library (Legacy)
    const libraryRef = collection(firestore, 'users', userId, 'library');
    const q = query(libraryRef, where('clientId', '==', clientId));
    const librarySnapshot = await getDocs(q);

    for (const doc of librarySnapshot.docs) {
      const data = doc.data();
      const size = data.size || 0;
      const type = data.type;

      usage.totalSize += size;
      usage.totalFiles++;

      if (type === 'cvs') {
        usage.cvCount++;
        usage.cvSize += size;
      } else if (type === 'caseStudiesReports') {
        usage.caseStudyCount++;
        usage.caseStudySize += size;
      } else if (type === 'companyInfoRates') {
        usage.companyInfoCount++;
        usage.companyInfoSize += size;
      } else if (type === 'pastProposals') {
        usage.pastProposalCount++;
        usage.pastProposalSize += size;
      }
    }

    // Fetch Knowledge Documents (New Smart RAG)
    const knowledgeRef = collection(firestore, 'clients', clientId, 'knowledge_documents');
    const knowledgeSnapshot = await getDocs(knowledgeRef);

    for (const doc of knowledgeSnapshot.docs) {
      const data = doc.data();
      const size = data.size || 0;
      const type = data.collectionType;

      usage.totalSize += size;
      usage.totalFiles++;

      if (type === 'people_cv') {
        usage.cvCount++;
        usage.cvSize += size;
      } else if (type === 'bidding_company_profile') {
        usage.caseStudyCount++;
        usage.caseStudySize += size;
      } else if (type === 'bidding_proposal' || type === 'procurement_rfp') {
        usage.pastProposalCount++;
        usage.pastProposalSize += size;
      }
    }

    return usage;
  } catch (_error) {
    throw new Error('Failed to fetch storage usage');
  }
};

/**
 * Get tender-specific storage usage
 */
export const getTenderStorageUsage = async (
  userId: string,
  tenderId: string,
  clientId: string
): Promise<TenderStorageUsage> => {
  if (!userId || !tenderId || !clientId) {
    throw new Error('User ID, Tender ID, and Client ID are required');
  }

  const usage: TenderStorageUsage = {
    supportDocsCount: 0,
    draftsCount: 0,
    cvCount: 0,
    formsCount: 0,
    totalSize: 0,
  };

  try {
    // Fetch support docs
    const supportDocsRef = collection(firestore, 'tenders', tenderId, 'library');
    const supportDocsQuery = query(supportDocsRef, where('clientId', '==', clientId));
    const supportDocsSnapshot = await getDocs(supportDocsQuery);
    for (const doc of supportDocsSnapshot.docs) {
      const data = doc.data();
      const size = data.size || 0;
      usage.supportDocsCount++;
      usage.totalSize += size;
    }

    // Fetch drafts for review
    const draftsRef = collection(firestore, 'tenders', tenderId, 'draftsForReview');
    const draftsQuery = query(draftsRef, where('clientId', '==', clientId));
    const draftsSnapshot = await getDocs(draftsQuery);
    for (const doc of draftsSnapshot.docs) {
      const data = doc.data();
      const size = data.size || 0;
      usage.draftsCount++;
      usage.totalSize += size;
    }

    // Fetch CVs
    const cvsRef = collection(firestore, 'tenders', tenderId, 'cvs');
    const cvsQuery = query(cvsRef, where('clientId', '==', clientId));
    const cvsSnapshot = await getDocs(cvsQuery);
    for (const doc of cvsSnapshot.docs) {
      const data = doc.data();
      const size = data.size || 0;
      usage.cvCount++;
      usage.totalSize += size;
    }

    // Fetch Forms
    const formsRef = collection(firestore, 'tenders', tenderId, 'forms');
    const formsQuery = query(formsRef, where('clientId', '==', clientId));
    const formsSnapshot = await getDocs(formsQuery);
    for (const doc of formsSnapshot.docs) {
      const data = doc.data();
      const size = data.size || 0;
      usage.formsCount++;
      usage.totalSize += size;
    }

    return usage;
  } catch (_error) {
    throw new Error('Failed to fetch tender storage usage');
  }
};

/**
 * Validate if files can be uploaded to library based on file count and size limits
 */
export const validateLibraryUpload = async (
  userId: string,
  clientId: string,
  files: File[],
  fileType: 'cvs' | 'pastProposals' | 'caseStudiesReports'
): Promise<ValidationResult> => {
  // Get current usage
  const usage = await getUserLibraryStorageUsage(userId, clientId);

  // Calculate new totals after upload
  const newFilesSize = files.reduce((sum, file) => sum + file.size, 0);
  const newTotalSize = usage.totalSize + newFilesSize;
  const newTotalFiles = usage.totalFiles + files.length;

  // Check individual file sizes
  const oversizedFiles = files.filter((file) => file.size > FILE_SIZE_LIMITS.MAX_FILE_SIZE);
  if (oversizedFiles.length > 0) {
    return {
      allowed: false,
      reason: `Some files exceed the maximum size limit of ${formatBytes(FILE_SIZE_LIMITS.MAX_FILE_SIZE)}. Please reduce file sizes and try again.`,
    };
  }

  // Check total storage limit
  if (isLimitExceeded(newTotalSize, FILE_SIZE_LIMITS.MAX_TOTAL_STORAGE)) {
    return {
      allowed: false,
      reason: `Upload would exceed your total storage limit of ${formatBytes(FILE_SIZE_LIMITS.MAX_TOTAL_STORAGE)}. Current usage: ${formatBytes(usage.totalSize)}. Please delete some files before uploading new ones.`,
      currentUsage: usage.totalSize,
      maxLimit: FILE_SIZE_LIMITS.MAX_TOTAL_STORAGE,
    };
  }

  // Check total file count limit
  if (isLimitExceeded(newTotalFiles, FILE_COUNT_LIMITS.MAX_LIBRARY_FILES_TOTAL)) {
    return {
      allowed: false,
      reason: `Upload would exceed your maximum file limit of ${FILE_COUNT_LIMITS.MAX_LIBRARY_FILES_TOTAL} files. Current count: ${usage.totalFiles}. Please delete some files before uploading new ones.`,
      currentUsage: usage.totalFiles,
      maxLimit: FILE_COUNT_LIMITS.MAX_LIBRARY_FILES_TOTAL,
    };
  }

  // Check category-specific file count limits
  let categoryLimit = 0;
  let categoryCount = 0;
  let categoryName = '';

  if (fileType === 'cvs') {
    categoryLimit = FILE_COUNT_LIMITS.MAX_CVS;
    categoryCount = usage.cvCount;
    categoryName = 'CVs';
  } else if (fileType === 'caseStudiesReports') {
    categoryLimit = FILE_COUNT_LIMITS.MAX_CASE_STUDIES;
    categoryCount = usage.caseStudyCount;
    categoryName = 'Case Studies';
  } else if (fileType === 'pastProposals') {
    categoryLimit = FILE_COUNT_LIMITS.MAX_PAST_PROPOSALS;
    categoryCount = usage.pastProposalCount;
    categoryName = 'Past Proposals';
  }

  if (isLimitExceeded(categoryCount + files.length, categoryLimit)) {
    return {
      allowed: false,
      reason: `Upload would exceed your ${categoryName} limit of ${categoryLimit} files. Current count: ${categoryCount}. Please delete some files before uploading new ones.`,
      currentUsage: categoryCount,
      maxLimit: categoryLimit,
    };
  }

  // Generate warning message if approaching limits
  let warningMessage: string | undefined;
  if (isApproachingLimit(newTotalSize, FILE_SIZE_LIMITS.MAX_TOTAL_STORAGE)) {
    const percentage = calculateUsagePercentage(newTotalSize, FILE_SIZE_LIMITS.MAX_TOTAL_STORAGE);
    warningMessage = `You're using ${percentage}% of your storage limit (${formatBytes(newTotalSize)} of ${formatBytes(FILE_SIZE_LIMITS.MAX_TOTAL_STORAGE)}).`;
  } else if (isApproachingLimit(newTotalFiles, FILE_COUNT_LIMITS.MAX_LIBRARY_FILES_TOTAL)) {
    const percentage = calculateUsagePercentage(
      newTotalFiles,
      FILE_COUNT_LIMITS.MAX_LIBRARY_FILES_TOTAL
    );
    warningMessage = `You're using ${percentage}% of your file limit (${newTotalFiles} of ${FILE_COUNT_LIMITS.MAX_LIBRARY_FILES_TOTAL} files).`;
  }

  return {
    allowed: true,
    warningMessage,
  };
};

/**
 * Validate if files can be uploaded to a tender
 */
export const validateTenderUpload = async (
  userId: string,
  tenderId: string,
  clientId: string,
  files: File[],
  uploadType: 'support' | 'draft' | 'cv' | 'tender_form'
): Promise<ValidationResult> => {
  // Get current tender usage
  const tenderUsage = await getTenderStorageUsage(userId, tenderId, clientId);

  // Get total user storage usage to check against overall limit
  const userUsage = await getUserLibraryStorageUsage(userId, clientId);

  // Calculate new totals
  const newFilesSize = files.reduce((sum, file) => sum + file.size, 0);
  const newTotalSize = userUsage.totalSize + tenderUsage.totalSize + newFilesSize;

  // Check individual file sizes
  const maxSize =
    uploadType === 'cv' || uploadType === 'tender_form'
      ? FILE_SIZE_LIMITS.MAX_GENERATION_SOURCE_FILE_SIZE
      : FILE_SIZE_LIMITS.MAX_FILE_SIZE;

  const oversizedFiles = files.filter((file) => file.size > maxSize);
  if (oversizedFiles.length > 0) {
    return {
      allowed: false,
      reason: `Some files exceed the maximum size limit of ${formatBytes(maxSize)}. Please reduce file sizes and try again.`,
    };
  }

  // Validate file types for generation sources
  if (uploadType === 'cv' || uploadType === 'tender_form') {
    const allowedExtensions = ['.pdf', '.docx', '.txt', '.md', '.csv', '.xls', '.xlsx'];
    const invalidFiles = files.filter((file) => {
      const lowerName = file.name.toLowerCase();
      return !allowedExtensions.some((ext) => lowerName.endsWith(ext));
    });

    if (invalidFiles.length > 0) {
      return {
        allowed: false,
        reason: `Some files have invalid extensions. Allowed types: ${allowedExtensions.join(', ')}.`,
      };
    }
  }

  // Check total storage limit (including library files)
  if (isLimitExceeded(newTotalSize, FILE_SIZE_LIMITS.MAX_TOTAL_STORAGE)) {
    return {
      allowed: false,
      reason: `Upload would exceed your total storage limit of ${formatBytes(FILE_SIZE_LIMITS.MAX_TOTAL_STORAGE)}. Current usage: ${formatBytes(userUsage.totalSize + tenderUsage.totalSize)}. Please delete some files before uploading new ones.`,
      currentUsage: userUsage.totalSize + tenderUsage.totalSize,
      maxLimit: FILE_SIZE_LIMITS.MAX_TOTAL_STORAGE,
    };
  }

  // Check tender-specific file count limits
  if (uploadType === 'support') {
    const newSupportDocsCount = tenderUsage.supportDocsCount + files.length;
    if (isLimitExceeded(newSupportDocsCount, FILE_COUNT_LIMITS.MAX_SUPPORT_DOCS_PER_TENDER)) {
      return {
        allowed: false,
        reason: `Upload would exceed the maximum of ${FILE_COUNT_LIMITS.MAX_SUPPORT_DOCS_PER_TENDER} support documents per tender. Current count: ${tenderUsage.supportDocsCount}.`,
        currentUsage: tenderUsage.supportDocsCount,
        maxLimit: FILE_COUNT_LIMITS.MAX_SUPPORT_DOCS_PER_TENDER,
      };
    }
  } else if (uploadType === 'draft') {
    const newDraftsCount = tenderUsage.draftsCount + files.length;
    if (isLimitExceeded(newDraftsCount, FILE_COUNT_LIMITS.MAX_DRAFTS_PER_TENDER)) {
      return {
        allowed: false,
        reason: `Upload would exceed the maximum of ${FILE_COUNT_LIMITS.MAX_DRAFTS_PER_TENDER} draft documents per tender. Current count: ${tenderUsage.draftsCount}.`,
        currentUsage: tenderUsage.draftsCount,
        maxLimit: FILE_COUNT_LIMITS.MAX_DRAFTS_PER_TENDER,
      };
    }
  } else if (uploadType === 'cv') {
    const newCvCount = tenderUsage.cvCount + files.length;
    if (isLimitExceeded(newCvCount, FILE_COUNT_LIMITS.MAX_TENDER_CVS)) {
      return {
        allowed: false,
        reason: `Upload would exceed the maximum of ${FILE_COUNT_LIMITS.MAX_TENDER_CVS} CVs per tender. Current count: ${tenderUsage.cvCount}.`,
        currentUsage: tenderUsage.cvCount,
        maxLimit: FILE_COUNT_LIMITS.MAX_TENDER_CVS,
      };
    }
  } else if (uploadType === 'tender_form') {
    const newFormsCount = tenderUsage.formsCount + files.length;
    if (isLimitExceeded(newFormsCount, FILE_COUNT_LIMITS.MAX_TENDER_FORMS)) {
      return {
        allowed: false,
        reason: `Upload would exceed the maximum of ${FILE_COUNT_LIMITS.MAX_TENDER_FORMS} forms per tender. Current count: ${tenderUsage.formsCount}.`,
        currentUsage: tenderUsage.formsCount,
        maxLimit: FILE_COUNT_LIMITS.MAX_TENDER_FORMS,
      };
    }
  }

  // Generate warning message if approaching limits
  let warningMessage: string | undefined;
  if (isApproachingLimit(newTotalSize, FILE_SIZE_LIMITS.MAX_TOTAL_STORAGE)) {
    const percentage = calculateUsagePercentage(newTotalSize, FILE_SIZE_LIMITS.MAX_TOTAL_STORAGE);
    warningMessage = `You're using ${percentage}% of your storage limit (${formatBytes(newTotalSize)} of ${formatBytes(FILE_SIZE_LIMITS.MAX_TOTAL_STORAGE)}).`;
  }

  return {
    allowed: true,
    warningMessage,
  };
};
